import { AfterViewInit, ChangeDetectionStrategy, Component, Signal, ViewChild, computed } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Store, select } from '@ngrx/store';
import { AuthorizeService } from '@platform/auth/authorize.service';
import { IOptionsStoreWithFilter, OptionsStoreService, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';
import { selectUser } from '@platform/platform.selectors';
import { go } from '@platform/router.actions';
import { filterSucceededResource } from '@platform/shared/resource.state';
import { RootState } from '@root/app.state';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom, interval, map, startWith } from 'rxjs';
import * as CoreActions from '@core/core.actions';
import * as PlatformActions from '@platform/platform.actions';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { compare } from '@core/customer-space-admin/utils/cs-user.utils';
import { Project } from '@apps/projects/projects.state';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { EmptyListComponent } from '@platform/components/empty-list/empty-list.component';
import { MatInputModule } from '@angular/material/input';
import { ShowAuthorizeDirective } from '@platform/auth/show-authorize.directive';
import { ProjectsService } from '../projects.service';

export type ProjectSelectType = 'MINE' | 'ACTIVE' | 'INACTIVE' | 'NONE';
export type ProjectSortType = 'PROJECT' | 'NOTIFICATIONS' | 'TASKS';

@Component({
  selector: 'app-project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
  imports: [
    CommonModule,
    MatSortModule,
    MatPaginatorModule,
    MatTableModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatBadgeModule,
    RouterModule,
    TranslateModule,
    ShowAuthorizeDirective,
    EmptyListComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectListComponent implements AfterViewInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  public readonly dataSource = new MatTableDataSource<Project>([]);

  public readonly filterForm = new FormGroup({
    search: new FormControl<string | null>(''),
    projectSelect: new FormControl<ProjectSelectType>('MINE'),
  });

  public readonly customerSpaceSelectionTypeTranslationKeyMapping: Record<ProjectSelectType, string> = {
    MINE: 'PROJECTS.project-list.my-projects',
    ACTIVE: 'PROJECTS.project-list.active-projects',
    INACTIVE: 'PROJECTS.project-list.inactive-projects',
    NONE: '-',
  };
  public readonly projectSelections = computed<ProjectSelectType[]>(() =>
    this.isPortalAdmin() ? ['MINE', 'ACTIVE', 'INACTIVE'] : ['MINE', 'INACTIVE'],
  );

  public readonly allowedCustomerSpaces: Signal<Project[] | undefined>;
  public readonly isInternalUser: Signal<boolean>;

  public showProjectNumbersDiv: boolean[];
  public showOverlay: boolean;

  private favoriteChanged$ = new BehaviorSubject(null); // not used

  private readonly isPortalAdmin: Signal<boolean>;
  private readonly optionsStore: IOptionsStoreWithFilter<{ projectSelect: ProjectSelectType }>;

  constructor(
    private store$: Store<RootState>,
    private authorizeService: AuthorizeService,
    optionsStoreService: OptionsStoreService,
    private translate: TranslateService,
    private projectsService: ProjectsService,
  ) {
    this.optionsStore = optionsStoreService.getOptionsStore<{ projectSelect: ProjectSelectType }>(UiTableKeys.ProjectsList, {
      projectSelect: 'MINE',
    });

    const projects$ = this.projectsService.getProjects();
    this.isPortalAdmin = toSignal(authorizeService.isAuthorized('PORTAL_ADMIN', null!), { initialValue: false });
    this.isInternalUser = toSignal(
      store$.pipe(
        select(selectUser),
        filterSucceededResource(),
        map((user) => user.isInternal),
      ),
      { initialValue: false },
    );

    combineLatest([projects$, this.filterForm.controls.projectSelect.valueChanges.pipe(startWith('MINE' as ProjectSelectType))])
      .pipe(takeUntilDestroyed())
      .subscribe(([projects, projectSelect]) => {
        this.dataSource.data = projects.filter((project) => isProjectSelected(project, projectSelect));
      });

    this.filterForm.controls.search.valueChanges.pipe(takeUntilDestroyed()).subscribe((searchValue) => {
      this.dataSource.filter = searchValue ?? '';
    });

    this.dataSource.filterPredicate = (item, filter) => item.name.toLowerCase().includes(filter.toLowerCase());
    this.dataSource.sortData = (items, sort) => [...items].sort((a, b) => compareProjects(a, b, sort));

    interval(1000 * 30)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.store$.dispatch(PlatformActions.loadCurrentUser());
        this.store$.dispatch(CoreActions.loadCustomerSpaces());
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.optionsStore.registerSort(this.sort);
    this.optionsStore.registerPaginatorPageSize(this.paginator);
    this.optionsStore.registerFilter('projectSelect', this.filterForm.controls.projectSelect);
  }

  public isAuthorized(project: Project): Observable<boolean> {
    const values = new Map<string, string>();
    values.set('customerSpaceId', project.customerSpaceId);
    return this.authorizeService.isAuthorized('COCKPIT_READ', values);
  }

  public toggleFavorite(project: Project): void {
    if (project.isFavorite) {
      this.store$.dispatch(CoreActions.deleteFavorite({ parentId: project.id }));
    } else {
      this.store$.dispatch(CoreActions.addFavorite({ parentId: project.id }));
    }

    project.isFavorite = !project.isFavorite;
    this.favoriteChanged$.next(null);
  }

  // public isMyCustomerSpace(customerSpace: CustomerSpace): boolean {
  //   return isMyCustomerSpace(customerSpace, this.allowedCustomerSpaces());
  // }

  public async navigateToCockpit(project: Project, $event: Event): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();
    if (await firstValueFrom(this.isAuthorized(project))) {
      this.store$.dispatch(go({ path: ['customer-spaces', project.customerSpaceId, 'cockpit'] }));
    }
  }

  public navigateToEdit(project: Project, $event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.store$.dispatch(
      go({
        path: ['customer-spaces', project.customerSpaceId, 'projects', project.id, 'admin'],
      }),
    );
  }

  public onProjectNumbersClick($event: Event, abacusLinkBase: string, abacusProjectNumbers: string[]) {
    const projectsCount = abacusProjectNumbers?.length;

    if (projectsCount <= 1) {
      this.openAbacusLink($event, abacusLinkBase, abacusProjectNumbers[0]);
      return;
    }

    if (this.isInternalUser()) {
      // if (!this.showProjectNumbersDiv[i]) {
      //   this.showDivWithOverlay(i);
      // }
    }

    $event.preventDefault();
    $event.stopPropagation();
  }

  public showDivWithOverlay(i: number) {
    this.showProjectNumbersDiv[i] = true;
    this.showOverlay = true;
  }

  public hideOverlayAndDiv() {
    this.showProjectNumbersDiv.fill(false);
    this.showOverlay = false;
  }

  public openAbacusLink($event: Event, abacusLinkBase: string, projectNumber: string) {
    $event.preventDefault();
    $event.stopPropagation();
    window.open(abacusLinkBase + projectNumber, '_blank');
    this.hideOverlayAndDiv();
  }

  public getTranslationForSelection(selection: ProjectSelectType) {
    switch (selection) {
      case 'NONE':
        return '-';
      case 'ACTIVE':
        return this.translate.instant('PROJECTS.project-list.active-projects');
      case 'MINE':
        return this.translate.instant('PROJECTS.project-list.my-projects');
      case 'INACTIVE':
        return this.translate.instant('PROJECTS.project-list.inactive-projects');
    }
  }

  public getTranslationForSorting(selection: ProjectSortType) {
    switch (selection) {
      case 'PROJECT':
        return this.translate.instant('PROJECTS.project-list.sorting.project');
      case 'NOTIFICATIONS':
        return this.translate.instant('PROJECTS.project-list.sorting.notifications');
      case 'TASKS':
        return this.translate.instant('PROJECTS.project-list.sorting.tasks');
    }
  }
}

// const isMyCustomerSpace = (customerSpace: CustomerSpace, allowedCustomerSpaces: CustomerSpace[] | undefined): boolean =>
//   allowedCustomerSpaces?.find((x) => x.id === customerSpace.id) !== undefined;

const isProjectSelected = (project: Project, projectSelection: ProjectSelectType | null): boolean => {
  switch (projectSelection) {
    case 'ACTIVE':
      return project.isActive;
    case 'INACTIVE':
      return !project.isActive;
    case 'MINE':
      return project.isAssignedToMe;
    case null:
      return project.isActive;
  }
  return false;
};

const compareProjects = (a: Project, b: Project, sort: MatSort) => {
  if (a.isFavorite !== b.isFavorite) {
    return a.isFavorite ? -1 : 1;
  }

  const isAsc = sort.direction === 'asc';

  switch (sort.active) {
    case 'name':
      return compare(a.name, b.name, isAsc);
    case 'tasks':
      return compare(a.openTaskCount, b.openTaskCount, isAsc);
    case 'notifications':
      return compare(a.notificationCount, b.notificationCount, isAsc);
    default:
      return 0;
  }
};
